import React, { useState, useRef } from 'react'
import { motion } from 'framer-motion'
import { Upload, File, Folder, Lock, Eye, EyeOff, Loader2 } from 'lucide-react'
import { useVault } from '../contexts/VaultContext'
import { validatePasswordStrength } from '../lib/crypto'
import toast from 'react-hot-toast'

const UploadSection = () => {
  const [selectedFiles, setSelectedFiles] = useState(null)
  const [selectedFolder, setSelectedFolder] = useState(null)
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [uploading, setUploading] = useState(false)
  
  const fileInputRef = useRef(null)
  const folderInputRef = useRef(null)
  
  const { uploadFile, uploadFolder, listFiles } = useVault()
  
  const passwordStrength = validatePasswordStrength(password)

  const handleFileSelect = (e) => {
    const files = e.target.files
    if (files && files.length > 0) {
      setSelectedFiles(files)
      setSelectedFolder(null)
      // Clear folder input
      if (folderInputRef.current) {
        folderInputRef.current.value = ''
      }
    }
  }

  const handleFolderSelect = (e) => {
    const files = e.target.files
    if (files && files.length > 0) {
      setSelectedFolder(files)
      setSelectedFiles(null)
      // Clear file input
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
    }
  }

  const handleUpload = async (e) => {
    e.preventDefault()
    
    if (!password) {
      toast.error('Please enter an encryption password')
      return
    }
    
    if (!passwordStrength.isStrong) {
      toast.error('Please use a stronger password')
      return
    }

    if (!selectedFiles && !selectedFolder) {
      toast.error('Please select files or a folder to upload')
      return
    }

    setUploading(true)

    try {
      if (selectedFiles) {
        // Upload single file
        const file = selectedFiles[0]
        await uploadFile(file, password)
        toast.success(`${file.name} uploaded successfully!`)
      } else if (selectedFolder) {
        // Upload folder
        const folderName = selectedFolder[0].webkitRelativePath.split('/')[0]
        await uploadFolder(selectedFolder, password, folderName)
      }

      // Reset form
      setSelectedFiles(null)
      setSelectedFolder(null)
      setPassword('')
      if (fileInputRef.current) fileInputRef.current.value = ''
      if (folderInputRef.current) folderInputRef.current.value = ''
      
      // Refresh file list
      await listFiles()
      
    } catch (error) {
      toast.error(error.message)
    } finally {
      setUploading(false)
    }
  }

  const getSelectionText = () => {
    if (selectedFiles) {
      return `File: ${selectedFiles[0].name}`
    }
    if (selectedFolder) {
      const folderName = selectedFolder[0].webkitRelativePath.split('/')[0]
      return `Folder: ${folderName} (${selectedFolder.length} files)`
    }
    return 'Select a file or folder to encrypt and upload'
  }

  const hasSelection = selectedFiles || selectedFolder

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="card bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-dashed border-blue-200"
    >
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
        {/* Upload Info */}
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
              <Upload className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">Upload New Item</h2>
              <p className="text-sm text-gray-600">
                {getSelectionText()}
              </p>
            </div>
          </div>
        </div>

        {/* Upload Buttons */}
        <div className="flex gap-3">
          <motion.label
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className="btn-primary cursor-pointer flex items-center gap-2"
          >
            <File className="w-4 h-4" />
            Select File
            <input
              ref={fileInputRef}
              type="file"
              onChange={handleFileSelect}
              className="hidden"
              disabled={uploading}
            />
          </motion.label>

          <motion.label
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className="btn-secondary cursor-pointer flex items-center gap-2"
          >
            <Folder className="w-4 h-4" />
            Select Folder
            <input
              ref={folderInputRef}
              type="file"
              webkitdirectory=""
              directory=""
              onChange={handleFolderSelect}
              className="hidden"
              disabled={uploading}
            />
          </motion.label>
        </div>
      </div>

      {/* Upload Form */}
      {hasSelection && (
        <motion.form
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          transition={{ duration: 0.3 }}
          onSubmit={handleUpload}
          className="mt-6 pt-6 border-t border-blue-200"
        >
          <div className="flex flex-col sm:flex-row gap-4">
            {/* Password Input */}
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Encryption Password
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="input-field pl-10 pr-10"
                  placeholder="Enter encryption password"
                  required
                  disabled={uploading}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  disabled={uploading}
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              
              {/* Password Strength */}
              {password && (
                <motion.div 
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="mt-2"
                >
                  <div className="flex gap-1 mb-1">
                    {[1, 2, 3, 4, 5].map((level) => (
                      <div
                        key={level}
                        className={`h-1 flex-1 rounded ${
                          level <= passwordStrength.score
                            ? 'bg-green-500'
                            : 'bg-gray-200'
                        }`}
                      />
                    ))}
                  </div>
                  {passwordStrength.feedback.length > 0 && (
                    <p className="text-xs text-gray-600">
                      {passwordStrength.feedback[0]}
                    </p>
                  )}
                  {passwordStrength.isStrong && (
                    <p className="text-xs text-green-600">
                      ✓ Password is valid (4+ characters)
                    </p>
                  )}
                </motion.div>
              )}
            </div>

            {/* Upload Button */}
            <div className="flex items-end">
              <motion.button
                type="submit"
                disabled={uploading || !passwordStrength.isStrong}
                whileHover={{ scale: uploading ? 1 : 1.02 }}
                whileTap={{ scale: uploading ? 1 : 0.98 }}
                className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed h-12 px-6 flex items-center gap-2"
              >
                {uploading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Encrypting...
                  </>
                ) : (
                  <>
                    <Lock className="w-4 h-4" />
                    Encrypt & Upload
                  </>
                )}
              </motion.button>
            </div>
          </div>
        </motion.form>
      )}
    </motion.div>
  )
}

export default UploadSection
